import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ArrowLeft,
  MoreVertical,
  Pencil,
  Trash2,
  Plus,
  TrendingUp,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import api from '@/lib/api'
import type { Account, ManualHolding } from '@/types'
import { RenameManualAccountDialog } from '@/components/manual/RenameManualAccountDialog'
import { UpdateValueDialog } from '@/components/manual/UpdateValueDialog'
import { HoldingDialog } from '@/components/manual/HoldingDialog'
import { ConfirmDialog } from '@/components/ConfirmDialog'

interface ManualAccountDetailPageProps {
  manualId: number
}

export function ManualAccountDetailPage({ manualId }: ManualAccountDetailPageProps) {
  const navigate = useNavigate()
  const [account, setAccount] = useState<Account | null>(null)
  const [holdings, setHoldings] = useState<ManualHolding[]>([])
  const [totalValue, setTotalValue] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const [renameOpen, setRenameOpen] = useState(false)
  const [valueOpen, setValueOpen] = useState(false)
  const [holdingDialogOpen, setHoldingDialogOpen] = useState(false)
  const [editingHolding, setEditingHolding] = useState<ManualHolding | null>(null)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const [holdingToDelete, setHoldingToDelete] = useState<ManualHolding | null>(null)
  const [isDeletingHolding, setIsDeletingHolding] = useState(false)

  const fetchAccount = useCallback(async () => {
    const response = await api.get('/accounts', {
      params: { include_hidden: true },
    })
    const found =
      (response.data.accounts as Account[]).find((a) => a.manual_id === manualId) ?? null
    setAccount(found)
    return found
  }, [manualId])

  const fetchHoldings = useCallback(async () => {
    const response = await api.get(`/manual_accounts/${manualId}/holdings`)
    setHoldings(response.data.holdings || [])
    setTotalValue(response.data.total_value || 0)
  }, [manualId])

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const found = await fetchAccount()
      if (found?.tracking_mode === 'holdings') {
        await fetchHoldings()
      }
    } catch (error) {
      console.error('Failed to load manual account:', error)
    } finally {
      setIsLoading(false)
    }
  }, [fetchAccount, fetchHoldings])

  useEffect(() => {
    load()
  }, [load])

  const trackingMode = account?.tracking_mode
  const balance =
    trackingMode === 'total_value' ? account?.manual_total_value ?? 0 : totalValue

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true)
    try {
      await api.delete(`/manual_accounts/${manualId}`)
      toast.success('Account removed')
      navigate('/accounts')
    } catch (error) {
      console.error('Failed to delete account:', error)
      toast.error('Unable to remove account')
      setIsDeletingAccount(false)
      setDeleteAccountOpen(false)
    }
  }

  const handleDeleteHolding = async () => {
    if (!holdingToDelete) {
      return
    }
    setIsDeletingHolding(true)
    try {
      await api.delete(`/manual_accounts/${manualId}/holdings/${holdingToDelete.id}`)
      toast.success('Holding deleted')
      setHoldingToDelete(null)
      await fetchHoldings()
    } catch (error) {
      console.error('Failed to delete holding:', error)
      toast.error('Unable to delete holding')
    } finally {
      setIsDeletingHolding(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-24" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="mb-4 h-6 w-40" />
            <Skeleton className="h-8 w-32" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!account) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/accounts')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Accounts
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Account not found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              This manual account may have been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" className="-ml-2 w-fit" onClick={() => navigate('/accounts')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Accounts
      </Button>

      {/* Header / summary */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                <TrendingUp className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {account.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {account.last_updated
                    ? `Updated ${new Date(account.last_updated).toLocaleDateString()}`
                    : 'Manual account'}
                </p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setRenameOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-danger"
                  onClick={() => setDeleteAccountOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="mt-4">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(balance)}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="secondary">
                {trackingMode === 'total_value' ? 'Total value' : 'Holdings'}
              </Badge>
              <Badge variant="outline">Manual</Badge>
            </div>
          </div>

          {trackingMode === 'total_value' && (
            <div className="mt-4">
              <Button variant="outline" onClick={() => setValueOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Update value
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Holdings */}
      {trackingMode === 'holdings' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Holdings
            </h2>
            <Button
              onClick={() => {
                setEditingHolding(null)
                setHoldingDialogOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Holding
            </Button>
          </div>

          {holdings.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <TrendingUp className="mb-4 h-12 w-12 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  No holdings yet
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Add stocks, ETFs, or other positions to track this account.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="divide-y divide-gray-100 p-0 dark:divide-gray-800">
                {holdings.map((holding) => (
                  <div
                    key={holding.id}
                    className="flex items-center justify-between gap-4 px-6 py-4"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {holding.ticker}
                      </p>
                      <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                        {holding.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(holding.value)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {holding.quantity} @ {formatCurrency(holding.price)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit holding"
                        onClick={() => {
                          setEditingHolding(holding)
                          setHoldingDialogOpen(true)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete holding"
                        className="text-danger"
                        onClick={() => setHoldingToDelete(holding)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <RenameManualAccountDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        accountId={manualId}
        currentName={account.name}
        onRenamed={(name) => {
          setAccount((prev) => (prev ? { ...prev, name } : prev))
          toast.success('Account renamed')
        }}
      />

      <UpdateValueDialog
        open={valueOpen}
        onOpenChange={setValueOpen}
        accountId={manualId}
        currentValue={account.manual_total_value ?? 0}
        onUpdated={(value) => {
          setAccount((prev) =>
            prev ? { ...prev, manual_total_value: value, balance: value } : prev
          )
          toast.success('Value updated')
        }}
      />

      <HoldingDialog
        open={holdingDialogOpen}
        onOpenChange={setHoldingDialogOpen}
        accountId={manualId}
        holding={editingHolding}
        onSaved={() => {
          toast.success(editingHolding ? 'Holding updated' : 'Holding added')
          fetchHoldings()
        }}
      />

      <ConfirmDialog
        open={deleteAccountOpen}
        onOpenChange={setDeleteAccountOpen}
        title="Remove account?"
        description="This permanently deletes the account and all of its holdings."
        confirmLabel="Remove"
        destructive
        isConfirming={isDeletingAccount}
        onConfirm={handleDeleteAccount}
      />

      <ConfirmDialog
        open={holdingToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setHoldingToDelete(null)
        }}
        title="Delete holding?"
        description={
          holdingToDelete
            ? `Remove ${holdingToDelete.ticker} from this account.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        isConfirming={isDeletingHolding}
        onConfirm={handleDeleteHolding}
      />
    </div>
  )
}
